// Shared mount/teardown for the no-iframe embed: attach an open Shadow DOM to
// the host element, inject the compiled renderer CSS, and render SeoEmbedApp
// with React 18. Used by both entries (newFrame.js bootstrap and the
// famewall-widget ESM module) so there is exactly one definition of "mount a
// Famewall embed into an element".

import React from 'react';
import { createRoot } from 'react-dom/client';

// Virtual module resolved by the esbuild build (scripts/build-seo-embed.mjs):
// the compiled Tailwind + renderer CSS as one string, injected into each
// embed's Shadow DOM.
import EMBED_CSS from 'seo-embed:styles';

import SeoEmbedApp from '../SeoEmbedApp';
import EmbedError from './EmbedError';

// One live React root per host element, so a re-mount (props changed) or an
// unmount (component lifecycle, SPA navigation) can tear the previous tree
// down cleanly. WeakMap: removed elements never leak.
const activeEmbeds = new WeakMap();

// Without a boundary, any render-time throw inside the tree unmounts the whole
// root and leaves an empty shadow DOM — the embed paints for a frame and then
// vanishes, which is both the worst-looking failure on a customer's page and
// the hardest to report. Contain it: log once for whoever is in DevTools, and
// render nothing (or the reason, when the embed opted into debug).
class EmbedBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false, message: '' };
  }

  static getDerivedStateFromError(error) {
    return {
      failed: true,
      message: (error && error.message) || 'Testimonials could not be rendered.',
    };
  }

  componentDidCatch(error) {
    try {
      console.error('[Famewall] embed render failed:', error);
    } catch (err) {
      // No console — nothing further to do.
    }
  }

  render() {
    if (this.state.failed) {
      return this.props.debug
        ? React.createElement(EmbedError, { message: this.state.message })
        : null;
    }

    return this.props.children;
  }
}

// Quirks mode (a page with no <!DOCTYPE>) lets a percentage height skip every
// auto-height ancestor until it finds one with a set height - or the viewport.
// The renderers' h-full wrappers and Swiper's `.swiper-wrapper { height: 100% }`
// then size off the host page instead of their content. Weebly's editor renders
// Embed Code in a doctype-less iframe (.custom-html-frame) that it keeps
// resizing to fit, so the embed fills the frame, the frame grows by the
// wrapper's padding, and that repeats forever (the published Weebly page has a
// doctype and renders fine). Blink, WebKit and Gecko all end that climb at a
// flex container, so in quirks mode our root becomes one and the tree inside
// resolves percentages as it does in standards mode. Standards-mode pages
// never take this branch.
function isQuirksMode(element) {
  const doc = element.ownerDocument || document;
  return doc.compatMode === 'BackCompat';
}

export function renderEmbed(element, config, apiOrigin) {
  if (!element) {
    return false;
  }

  let shadow = element.shadowRoot;
  if (!shadow) {
    if (typeof element.attachShadow !== 'function') {
      return false;
    }
    shadow = element.attachShadow({ mode: 'open' });
  }

  // Idempotent: a second render on the same element is a clean swap, not a
  // second tree stacked under the first.
  teardownEmbed(element);
  while (shadow.firstChild) {
    shadow.removeChild(shadow.firstChild);
  }

  const style = document.createElement('style');
  style.textContent = EMBED_CSS;
  shadow.appendChild(style);

  const mountPoint = document.createElement('div');
  mountPoint.className = 'famewall-embed-root';
  if (isQuirksMode(element)) {
    mountPoint.style.display = 'flex';
    mountPoint.style.flexDirection = 'column';
  }
  shadow.appendChild(mountPoint);

  const root = createRoot(mountPoint);
  root.render(
    React.createElement(
      EmbedBoundary,
      { debug: !!(config && config.debug) },
      React.createElement(SeoEmbedApp, {
        config: config,
        apiOrigin: apiOrigin,
        shadowHost: element,
      }),
    ),
  );

  activeEmbeds.set(element, root);
  return true;
}

export function teardownEmbed(element) {
  const root = element ? activeEmbeds.get(element) : null;

  if (!root) {
    return false;
  }

  activeEmbeds.delete(element);

  try {
    root.unmount();
  } catch (err) {
    // The tree may already be gone (host removed from the DOM) — teardown
    // must never throw into the host page.
  }

  if (element.shadowRoot) {
    while (element.shadowRoot.firstChild) {
      element.shadowRoot.removeChild(element.shadowRoot.firstChild);
    }
  }

  return true;
}
